import { useDeferredValue, useMemo, useState } from 'react';
import { Avatar, PersonName } from '../../components/Avatar';
import { Dialog } from '../../components/Dialog';
import { lifeYears } from '../../domain/dates';
import { searchTerms, similarity } from '../../domain/names';
import type { PersonWithNames, UUID } from '../../domain/types';
import { useI18n } from '../../i18n';

export function FindPersonDialog({
  open,
  onClose,
  people,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  people: PersonWithNames[];
  onPick: (id: UUID) => void;
}) {
  const { t, lang } = useI18n();
  const [q, setQ] = useState('');

  // The box keeps up with the fingers; the search runs a moment behind them (see PersonPicker).
  const asked = useDeferredValue(q);
  const results = useMemo(() => {
    const query = asked.trim().toLowerCase();
    if (!query) return [];
    type Reason = 'name' | 'nickname' | 'previous' | 'hebrew' | 'english';
    return people
      .map((p) => {
        let best = 0;
        let reason: Reason = 'name';
        for (const n of p.names) {
          const terms: Array<[string, Reason]> = [
            [`${n.given} ${n.family}`, n.language === 'he' ? 'hebrew' : 'english'],
            [n.nickname ?? '', 'nickname'],
            ...(n.previousNames ?? []).map((pn): [string, Reason] => [pn, 'previous']),
          ];
          for (const [term, kind] of terms) {
            if (!term) continue;
            const lower = term.toLowerCase();
            const s = lower.includes(query) ? 1 : lower.startsWith(query) ? 0.95 : similarity(lower, query);
            if (s > best) {
              best = s;
              reason = kind;
            }
          }
        }
        for (const term of searchTerms(p)) {
          const s = term.includes(query) ? 0.99 : similarity(term, query);
          if (s > best) best = s;
        }
        return { p, best, reason };
      })
      .filter((x) => x.best >= 0.45)
      .sort((a, b) => b.best - a.best)
      .slice(0, 20);
  }, [asked, people]);

  return (
    <Dialog open={open} onClose={onClose} title={t('find.title')}>
      <div className="field">
        <label className="sr-only" htmlFor="find-input">
          {t('find.title')}
        </label>
        <input
          id="find-input"
          className="input"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('find.placeholder')}
          autoComplete="off"
        />
      </div>
      <ul className="list-reset mt-2" style={{ maxHeight: '48vh', overflowY: 'auto' }}>
        {results.map(({ p, reason }) => (
          <li key={p.id}>
            <button
              type="button"
              className="person-row"
              onClick={() => {
                onPick(p.id);
                onClose();
                setQ('');
              }}
            >
              <Avatar person={p} lang={lang} size="md" />
              <span className="grow">
                <span className="name">
                  <PersonName person={p} lang={lang} />
                </span>
                <span className="sub">
                  {lifeYears(p.birth, p.death, p.status === 'deceased', lang)}
                  {reason !== 'english' && reason !== 'hebrew' ? ` · ${t(`find.reason.${reason}` as 'find.reason.name')}` : ''}
                </span>
              </span>
            </button>
          </li>
        ))}
        {q.trim() && results.length === 0 ? <li className="empty">{t('find.noResults', { q })}</li> : null}
      </ul>
    </Dialog>
  );
}
